import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { ChevronDown, Save } from 'lucide-react';
import { toEnum } from '../utils/clinicalSetting';

// Reusable input with optional className merge
const FormInput = ({ label, className = '', ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </label>
    <input
      {...props}
      className={`w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${className}`}
    />
  </div>
);

const FormSelect = ({ label, children, ...props }) => (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </label>
    <select
      {...props}
      className="appearance-none w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    >
      {children}
    </select>
    <ChevronDown className="absolute right-4 top-10 h-5 w-5 text-gray-400 pointer-events-none" />
  </div>
);

export default function AddPatient() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Canonical enum from localStorage (set by Dashboard)
  const setting = toEnum(localStorage.getItem('clinicalSetting') || 'inpatient');

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', dob: '', roomNumber: '',
    bedNumber: '', age: '', sex: '', chiefComplaint: '', tags: '',
  });

  const calculateAge = (dobStr) => {
    if (!dobStr) return '';
    const ms = Date.now() - new Date(dobStr).getTime();
    const d = new Date(ms);
    return Math.abs(d.getUTCFullYear() - 1970);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dob') {
      const age = calculateAge(value);
      setFormData(prev => ({ ...prev, dob: value, age: age.toString() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('You must be logged in to add a patient.');
      return;
    }

    const tagsArray = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    const payload = {
      ...formData,
      clinicalSetting: setting,          // canonical (Option A)
      setting,                           // legacy key for older iOS decoders
      tags: tagsArray,
      dob: formData.dob ? new Date(formData.dob) : null,
      age: Number(formData.age || 0),
      ownerId: currentUser.uid,
      dateAdded: new Date(),
    };

    try {
      const docRef = await addDoc(collection(db, 'patients'), payload);
      navigate(`/patient/${docRef.id}/detail`);
    } catch (err) {
      console.error('Error adding patient:', err);
      alert('Failed to add patient.');
    }
  };

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              New {setting === 'emergencyRoom' ? 'Emergency Room' : setting.charAt(0).toUpperCase() + setting.slice(1)} Patient
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Enter the patient's details below.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} />
                <FormInput label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Date of Birth" type="date" name="dob" value={formData.dob} onChange={handleChange} />
                <FormInput label="Age" name="age" type="number" value={formData.age} readOnly className="!bg-slate-200/50 dark:!bg-zinc-800/50 cursor-not-allowed" />
              </div>
              <FormSelect label="Sex" name="sex" value={formData.sex} onChange={handleChange} required>
                <option value="">Select Sex...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </FormSelect>
            </section>

            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">Visit Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Room Number" name="roomNumber" value={formData.roomNumber} onChange={handleChange} required />
                <FormInput label="Bed (Optional)" name="bedNumber" value={formData.bedNumber} onChange={handleChange} />
              </div>
              <FormInput label="Chief Complaint" name="chiefComplaint" value={formData.chiefComplaint} onChange={handleChange} required />
              <FormInput label="Tags (comma-separated)" name="tags" value={formData.tags} onChange={handleChange} placeholder="e.g., stroke, hypertension" />
            </section>

            <div className="flex justify-end gap-4 pt-6">
              <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 bg-slate-200 dark:bg-zinc-700 text-gray-800 dark:text-white rounded-full font-semibold hover:bg-slate-300 dark:hover:bg-zinc-600 transition">
                Cancel
              </button>
              <button type="submit" className="flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 shadow transition">
                <Save size={18} />
                Save Patient
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
